import React from 'react';

/**
 * GuestRouteBoundary ✦ (iter186)
 * ───────────────────────────────────────────────
 * Replaces the blank white screen the user saw when a guest
 * product route failed to mount (network glitch, lazy-load
 * failure, render-time exception). Renders a calm editorial
 * fallback with the constitutional copy:
 *
 *     "This moment is still arriving ✦"
 *
 * Same visual language as the rest of the editorial guest
 * surface — walnut/ivory on the existing midnight backdrop.
 */
export class GuestRouteBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Surface for production triage; never silently swallow.
    // eslint-disable-next-line no-console
    console.error('[GuestRouteBoundary]', error, info?.componentStack);
  }

  handleReturn = () => {
    // Prefer parent dashboard if we know the slug; else history back.
    const { fallbackHref } = this.props;
    if (fallbackHref) {
      window.location.assign(fallbackHref);
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.assign('/');
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="guest-route-boundary"
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: 'radial-gradient(circle at top, #1a1a2e, #15161B)' }}
      >
        <div className="text-center max-w-md">
          <p
            className="text-white mb-3"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 24, letterSpacing: '-0.01em' }}
          >
            This moment is still arriving ✦
          </p>
          <p className="text-white/50 text-sm mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            A small pause in the evening — your place is still here.
          </p>
          <button
            type="button"
            data-testid="guest-route-boundary-return"
            onClick={this.handleReturn}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-white"
            style={{
              background: 'linear-gradient(135deg, #B89762, #8C6A2B)',
              boxShadow: '0 4px 18px rgba(184,151,98,0.25)',
            }}
          >
            Return to the evening
          </button>
        </div>
      </div>
    );
  }
}

export default GuestRouteBoundary;
